//#region Util Imports

import * as _ from 'underscore';
import * as moment from 'moment';

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion


import { ColumnModel } from '../../../api/api.models'
import { Enums } from '../../../../models/common'
import { IOpData, IOpObject } from '../../../../models/util/interfaces'
import { Api, LocalDictionaryFactory } from '../../../api/api.module'
import { CommonCreateEdit, CreateEditField, CreateEditFieldSettings, CreateEditSection } from '../../common.create-edit'
import { GridService } from '../../../../services/grid'
import { IUserSession, TypeHelper } from '../../../../helpers';
import { IAlarmDefinitionsParameters, AlarmDefinitionsParametersOptions } from './alarm-definitions-parameters.interfaces'

// #region IAlarmDefinitionsParametersScope
export interface IAlarmDefinitionsParametersScope
    extends ng.IScope {
    section: CreateEditSection
    alarmDefParamsOptions: DevExpress.ui.dxFormOptions
    isVisible: () => boolean
}
// #endregion

// #region FormFieldsCollection
/**
* This is a backward compatiblity class that enforces static type.
* */
type FormFieldsCollection = {
    [key: number]: CreateEditField
}
// #endregion

// #region DataTypeValueTuple
/**
* This is a backward compatiblity class that enforces static type.
* */
type DataTypeValueTuple = {
    /**
     * IdDataType
     * */
    dataField: number,

    /**
     * Value
     * */
    value: any
}
// #endregion

export class AlarmDefinitionsParametersController
    extends CommonCreateEdit
    implements ng.IComponentController
    , IAlarmDefinitionsParameters {
    // #region Consts

    private static get FormType(): string { return 'Form' }
    private static get FormDataField(): string { return 'section.formData' }
    private static get FormItemsField(): string { return 'section.items' }

    // #endregion

    // #region Members

    public options: AlarmDefinitionsParametersOptions

    private formFields: FormFieldsCollection
    private editedObject: IOpObject

    // #endregion

    // #region ctor

    constructor(
        protected $scope: IAlarmDefinitionsParametersScope,
        protected $q: ng.IQService,
        protected $timeout: ng.ITimeoutService,
        protected api: Api,
        protected localDictionary: LocalDictionaryFactory,
        private gridService: GridService,
        protected userSession: IUserSession
    ) {
        super($scope, api, localDictionary, $q, $timeout, userSession)
    }

    // #endregion

    // #region ng.IComponentController implementation

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    // #endregion

    // #region IAlarmDefinitionsParameters implementation

    public setDataSource(opObject: IOpObject): void {
        this.editedObject = opObject

        if (this.editedObject != null
            && this.editedObject.dataList != null
            && this.$scope != null
            && this.$scope.section != null
            && this.$scope.section.formData != null
        ) {
            this.editedObject
                .dataList
                .toArray()
                .forEach((dItem: IOpData) => {
                    const key: string = dItem.idDataType.toString()
                    if (_.has(this.$scope.section.formData, key)) {
                        this.$scope.section.formData[key] = dItem.value
                        this.formFields[key] = dItem.value
                    }
                })
        }
    }

    public hasParameters(): boolean {
        return this.$scope != null
            && this.$scope.section != null
            && this.$scope.section.columns != null
            && this.$scope.section.columns.length > 0
    }

    public validateFormData(): boolean {
        if (this.$scope.section != null && this.$scope.section.form != null) {
            const result: DevExpress.ui.dxValidationGroupResult = this.$scope.section.form.validate()

            if (result != null) {
                return result.isValid
            }
        }

        return true
    }

    // #endregion

    // #region Setup

    private initialize(): void {
        this.setFlags()

        this.setSectionOptions()
            .then(() => {
                this.setFormOptions()
            })
            .always(() => {
                this.onInitialized()
            })
    }

    private setFlags(): void {
        this.$scope.isVisible = () => { return this.isVisible() }
    }

    private setSectionOptions(): JQueryPromise<void> {
        this.$scope.section = <any>{}

        const dataTypeGroupOptions: any = {
            skipView: true,
            groupReferenceType: this.options.parametersReferenceType,
            groupReferenceValue: this.options.parametersReferenceValue
        }

        return this.gridService
            .getColumnModelByCategory(Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__ALARM_DEF_PANEL_PARAMS,
                this.options.moduleName,
                dataTypeGroupOptions
            )
            .then((columns: ColumnModel[]) => {
                this.$scope.section.columns = _.size(columns) > 0
                    ? columns.map((column: ColumnModel) => <CreateEditFieldSettings>column)
                    : []
            })
    }

    private setFormOptions(): void {
        this.formFields = {}

        if (this.$scope.alarmDefParamsOptions != null) {
            this.$scope.alarmDefParamsOptions = null
        }

        this.$timeout(() => {
            this.$scope.alarmDefParamsOptions = {
                requiredMark: '*',
                labelLocation: 'top',
                width: '100%',
                height: '100%',
                colCount: 3,
                showRequiredMark: true,
                scrollingEnabled: true,

                bindingOptions: {
                    formData: AlarmDefinitionsParametersController.FormDataField,
                    items: AlarmDefinitionsParametersController.FormItemsField,
                },

                onInitialized: (args: any) => { this.onAlarmDefParamsInitialized(args) },
                onFieldDataChanged: (args: any) => { this.onAlarmDefParamsFieldDataChanged(args) },
            }
        }, 100)
    }

    // #endregion

    // #region Events

    private onInitialized(): void {
        if (this.options != null && this.options.onInitialized != null) {
            this.options.onInitialized(this)
        }
    }

    private onParameterValueChanged(idDataType: number, indexNbr: number, value: any, oldValue: any): void {
        if (this.options != null && this.options.onParameterValueChanged != null) {
            this.options.onParameterValueChanged({
                source: this,
                idDataType: idDataType,
                indexNbr: indexNbr,
                newValue: value,
                oldValue: oldValue
            })
        }
    }

    private onAlarmDefParamsInitialized(args: any): void {
        this.$timeout(() => {
            this.$scope.section.form = args.component
            this.loadForm(this.$scope.section, this.formFields, AlarmDefinitionsParametersController.FormType, this.options.referenceType, true)
        })
    }

    private onAlarmDefParamsFieldDataChanged(args: any): void {
        if (args == null) {
            return
        }

        const valueTuple: DataTypeValueTuple = args
        let oldValue: any = null

        if (this.formFields[valueTuple.dataField] != null) {
            oldValue = this.formFields[valueTuple.dataField].value

            if (this.formFields[valueTuple.dataField].typeId == Enums.DataTypeClass.Datetime || this.formFields[valueTuple.dataField].typeId == Enums.DataTypeClass.Date) {
                this.editedItems[valueTuple.dataField] = moment(oldValue).toDate().getTime() != valueTuple.value.getTime()
            } else {
                this.editedItems[valueTuple.dataField] = oldValue != valueTuple.value
            }
        }

        if ((valueTuple != null && valueTuple.value != null) || oldValue != null) {
            this.onParameterValueChanged(valueTuple.dataField, 0, valueTuple.value, oldValue)
        }
    }

    // #endregion

    // #region Flags

    private isVisible(): boolean {
        return this.hasParameters()
    }

    // #endregion
}
